import { t } from "ttag";

import { useConsultationStore } from "@/features/consultations/contexts";
import { useBoolean } from "@/shared/hooks";
import { CONSULTATION_PURPOSE } from "@/shared/models/legacy-constants";
import {
  Badge,
  checkableStyles,
  DocumentAddIcon,
  RadioGroup,
  VideoCameraIcon,
} from "@/shared/ui";
import { Heading } from "@/shared/ui/common/heading";
import { MedicationRefillModal, VideoConsultationModal } from "./components";

interface PurposeOptionsProps {
  isRefillDisabled: boolean;
  isAsyncDisabledForTirzepatide: boolean;
}

const purposeOptionCardClassName = checkableStyles().itemRoot({
  isCard: true,
  className:
    "group flex flex-col gap-8 rounded-2xl p-4 lg:w-full has-disabled:opacity-50",
});

export const PurposeOptions = ({
  isRefillDisabled,
  isAsyncDisabledForTirzepatide,
}: PurposeOptionsProps) => {
  const purpose = useConsultationStore((state) => state.purpose);
  const setPurpose = useConsultationStore((state) => state.setPurpose);

  const {
    value: showVideoConsultationModal,
    setTrue: openVideoConsultationModal,
    setFalse: closeVideoConsultationModal,
  } = useBoolean(false);
  const {
    value: showMedicationRefillModal,
    setTrue: openMedicationRefillModal,
    setFalse: closeMedicationRefillModal,
  } = useBoolean(false);

  const defaultCheckedValue =
    isRefillDisabled || isAsyncDisabledForTirzepatide
      ? CONSULTATION_PURPOSE.CONSULTATION
      : CONSULTATION_PURPOSE.REFILL;

  const asyncDisabled = isRefillDisabled || isAsyncDisabledForTirzepatide;

  return (
    <RadioGroup.Root
      className="flex flex-col gap-4 lg:grid lg:grid-cols-2"
      defaultValue={defaultCheckedValue}
      value={purpose}
      onValueChange={setPurpose}
    >
      <label className={purposeOptionCardClassName}>
        <div className="flex grow flex-col gap-3">
          <div className="flex justify-between">
            <DocumentAddIcon className="size-9 text-primary-900" />

            <div className="flex gap-2">
              <Badge color="dark-primary" size="small">
                {t`Fastest`}
              </Badge>
              <RadioGroup.Indicator
                value={CONSULTATION_PURPOSE.REFILL}
                disabled={asyncDisabled}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2 text-left text-neutral-900">
            <Heading level={3}>{t`Order medication`}</Heading>
            <p className="text-sm font-semibold">{t`Low on your meds? you can have your prescription within 2-3 days`}</p>
            <p className="text-sm">{t`No need for a video consultation! Just fill out the form, and we'll take care of everything. We're here to ensure the smoothest experience.`}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={openMedicationRefillModal}
          className="self-start text-sm text-primary-950 underline underline-offset-2"
        >
          {t`More info on medication refills`}
        </button>
      </label>

      <label className={purposeOptionCardClassName}>
        <div className="flex grow flex-col gap-3">
          <div className="flex justify-between">
            <VideoCameraIcon className="size-9 text-primary-900" />

            <RadioGroup.Indicator value={CONSULTATION_PURPOSE.CONSULTATION} />
          </div>
          <div className="flex grow flex-col gap-2 text-left text-neutral-900">
            <Heading level={3}>{t`Video consultation`}</Heading>
            <p className="text-sm font-semibold">{t`Looking for some one-on-one time with a provider?`}</p>
            <p className="text-sm">{t`By opting for a video consultation, you can schedule an appointment to see a provider. Don't worry about the details; we'll be here to help you find the perfect time slot for your visit.`}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={openVideoConsultationModal}
          className="self-start text-sm text-primary-950 underline underline-offset-2"
        >
          {t`More info on video consultations`}
        </button>
      </label>

      <MedicationRefillModal
        show={showMedicationRefillModal}
        onClose={closeMedicationRefillModal}
      />
      <VideoConsultationModal
        show={showVideoConsultationModal}
        onClose={closeVideoConsultationModal}
      />
    </RadioGroup.Root>
  );
};
